import * as L from 'leaflet';
import * as esri from 'esri-leaflet';

import { JsonFeatureIdentifier } from '../featureDisplay/jsonFeatureIdentifier';
import { ImageOverlayLayer } from './imageOverlayLayer.abstract';
import { FeatureDisplayItemGenerator } from '../featureDisplay/featureDisplayItemGenerator';

/*
export interface EsriDynamicMapLayerOptions {
  url: string;
  layers: number[];
  token?: string;
  zIndex?: number;
  opacity?: number;
  pane?: string;
  type?: string;
}*/

class DefaultJsonFeatureIdentifier extends JsonFeatureIdentifier {
  constructor(
    protected layer: EsriDynamicLayer,
  ) {
    super(
      layer,
      (thisLayer: EsriDynamicLayer, clickEvent: L.LeafletMouseEvent) => this.createUrl(thisLayer, clickEvent),
    );
    this.setResponseResultsExtractor((res: Record<string, unknown>) => res.results as Array<Record<string, unknown>>);
    this.setResultDetailsExtractor((featureDetails: Record<string, unknown>) => {
      const returnMap = new Map<string, string>();
      const attributes = featureDetails.attributes as Record<string, unknown>;
      Object.keys(attributes).forEach((key: string) => {
        returnMap.set(key, String(attributes[key]));
      });
      return returnMap;
    });
  }

  protected createUrl(
    layer: EsriDynamicLayer,
    clickEvent: L.LeafletMouseEvent,
  ): null | string {
    const bgsLeaflet = layer.getBgsLeaflet();
    if (null != bgsLeaflet) {
      const merc = L.Projection.SphericalMercator.project(clickEvent.latlng);
      const dpi = 96;
      const bounds = bgsLeaflet.getMapExtent();
      const mapSize = bgsLeaflet.getMapSize();

      const lowerLeftCoord = L.latLng([bounds.getMinLat(), bounds.getMinLon()]);
      const lowerLeftCoordSM = L.Projection.SphericalMercator.project(lowerLeftCoord);
      const topRightCoord = L.latLng([bounds.getMaxLat(), bounds.getMaxLon()]);
      const topRightCoordSM = L.Projection.SphericalMercator.project(topRightCoord);
      const bbox = [
        lowerLeftCoordSM.x,
        lowerLeftCoordSM.y,
        topRightCoordSM.x,
        topRightCoordSM.y,
      ];

      const parameters = new Array<string>();
      parameters.push('f=json');
      parameters.push(`geometry={"x":${merc.x},"y":${merc.y}}`);
      parameters.push('geometryType=esriGeometryPoint');
      parameters.push(`imageDisplay=${mapSize.widthPx},${mapSize.heightPx},${dpi}`);
      const layers = layer.options.get<Array<string>>('layers');
      const firstLayer = ((null != layers) && (layers.length > 0)) ? layers[0] : '';
      parameters.push(`layers=${firstLayer}`);
      parameters.push(`mapExtent=${bbox.join(',')}`);
      parameters.push('returnGeometry=false');
      parameters.push('sr=102100');
      parameters.push('tolerance=2');
      parameters.push(`token=${String(layer.options.get('token'))}`);

      const url = `${String(layer.options.get('url'))}/identify?${parameters.join('&')}`;
      return url.replace(/ /g, '');
    } else {
      return null;
    }
  }

}

export class EsriDynamicLayer extends ImageOverlayLayer {
  constructor(id: string, name?: string) {
    super(id, name);
  }

  public getLeafletLayer(): Promise<L.Layer> {
    return Promise.resolve(esri.dynamicMapLayer(this.options.getAll() as unknown as esri.DynamicMapLayerOptions));
  }

  public setFeatureIdentifiable(featureDisplayItemGen?: FeatureDisplayItemGenerator): this {
    this.setLayerClickFeatureItemGenerator(
      (featureDisplayItemGen)
        ? featureDisplayItemGen
        : new DefaultJsonFeatureIdentifier(this)
    );
    return this;
  }
}
